import { useCallback, useContext, useMemo } from 'react';
import { css } from '@emotion/react';
import { CreateResponse } from '@weave/schema-gen-ts/dist/schemas/phone/callgroup/v1/callgroup_service.pb';
import { Ringtone } from '@weave/schema-gen-ts/dist/shared/phonedata/v1/tenant.pb';
import { CallGroupApi } from '@frontend/api-call-group';
import { CallRouteV1 } from '@frontend/api-call-route';
import { DevicesQueries } from '@frontend/api-devices';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useScopedQuery } from '@frontend/scope';
import { sentry } from '@frontend/tracking';
import { validateName } from '@frontend/validation';
import {
  Button,
  DropdownField,
  InlineNotification,
  SkeletonLoaders,
  TextField,
  useAlert,
  useForm,
  useMultiStepModalControl,
  useModalControl,
} from '@frontend/design-system';
import { queryKeys } from '../../../../query-keys';
import { CreateCallGroupModal } from '../../../call-group/new/create-call-group-modal';
import { CallGroupSettingTray } from '../../call-group-setting-tray';
import {
  RingtoneDropdown,
  type RingtoneOption,
  DEFAULT_RINGTONE_VALUE,
} from './atoms/ringtone-dropdown/ringtone-dropdown';
import { filterRingtonesForDevices, getConsolidatedDisplayName } from './atoms/ringtone-dropdown/ringtone-utils';
import { Step } from './atoms/step';
import { CallerLabelBaseText, getRemainingCharacters } from './characters-helpers';
import { DevicesPreview, DeviceType, PreviewDeviceObject } from './devices-preview';
import { StepContext } from './provider';
import { AddStepPanelProps } from './types';

export const CallGroupStep = ({
  settingsTenantLocation,
  initialState,
  callObjectsData,
  onProceedClick,
}: AddStepPanelProps<CallRouteV1.Types.NodeType.NODE_TYPE_CALL_GROUP>) => {
  const { t } = useTranslation('phone');
  const alerts = useAlert();
  const stepContext = useContext(StepContext);

  const { triggerProps, ...createCallGroupModalProps } = useMultiStepModalControl(
    { onComplete: () => {}, onCancel: () => {}, showCancelAction: true },
    { onClose: () => {} }
  );

  const callGroupTrayModalControl = useModalControl();

  const handleOpenCreateCallGroup = () => {
    createCallGroupModalProps.goToStep({ orIndex: 0 });
    triggerProps.onClick();
  };

  const {
    callGroups,
    callGroupsIsLoading,
    sipProfiles,
    callForwardingNumbers,
    devices: allDevices = [],
  } = callObjectsData;

  const { data: ringtones = [] } = DevicesQueries.useRingtones({}, { select: (data) => data.ringtones, retry: false });

  // Filter to devices that have a recognized make. Devices with an empty or
  // "unspecified" make carry no useful info about which ringtones to offer.
  const devicesWithKnownMake = useMemo(
    () =>
      allDevices.filter((device) => {
        const make = (device.make ?? '').toLowerCase();
        return make !== '' && make !== 'unspecified';
      }),
    [allDevices]
  );

  // When no devices have a known make (no devices exist, or every device has an
  // empty/unspecified make), fall back to generic Yealink ringtones so the
  // dropdown always has options to display.
  const hasYealinkDevice = useMemo(
    () =>
      devicesWithKnownMake.length === 0 ||
      devicesWithKnownMake.some((device) => device.make.toLowerCase() === 'yealink'),
    [devicesWithKnownMake]
  );
  const hasPolycomDevice = useMemo(
    () => devicesWithKnownMake.some((device) => device.make.toLowerCase() === 'polycom'),
    [devicesWithKnownMake]
  );

  const filteredRingtones: RingtoneOption[] = useMemo(
    () => filterRingtonesForDevices(ringtones, { hasYealinkDevice, hasPolycomDevice }),
    [ringtones, hasYealinkDevice, hasPolycomDevice]
  );

  // Create a simple location resolver for call group devices
  const locationResolver = useMemo(() => {
    const getDeviceLocation = (deviceId: string): string => {
      const sipProfile = sipProfiles?.find((profile) => profile.device?.deviceId === deviceId);
      if (sipProfile?.device?.location?.locationId && settingsTenantLocation) {
        const location = settingsTenantLocation.children?.find(
          (loc) => loc.locationId === sipProfile.device?.location?.locationId
        );
        return location?.name || '';
      }
      return '';
    };

    const getForwardingNumberLocation = (forwardingNumberId: string): string => {
      const forwardingNumber = callForwardingNumbers?.find((fn) => fn.id === forwardingNumberId);
      if (forwardingNumber?.Labels?.[0]?.Value && settingsTenantLocation) {
        const location = settingsTenantLocation.children?.find(
          (loc) => loc.locationId === forwardingNumber?.Labels?.[0]?.Value
        );
        return location?.name || '';
      }
      return '';
    };

    return {
      getDeviceLocation,
      getForwardingNumberLocation,
    };
  }, [sipProfiles, callForwardingNumbers, settingsTenantLocation]);

  const { getFieldProps, isComplete, values } = useForm({
    fields: {
      callGroupId: {
        required: true,
        type: 'dropdown',
        value: initialState?.callObject?.primitiveId,
      },
      callerLabel: {
        required: false,
        type: 'text',
        value: initialState?.callObject?.callGroupExpansion?.callerLabel,
        validator: validateName,
        validateOnChange: true,
      },
      ringtone: {
        type: 'dropdown',
        required: false,
        value:
          (initialState?.callObject?.callGroupExpansion?.ringtone?.ringtoneType ?? Ringtone.RINGTONE_UNSPECIFIED) ===
          Ringtone.RINGTONE_UNSPECIFIED
            ? DEFAULT_RINGTONE_VALUE
            : initialState?.callObject?.callGroupExpansion?.ringtone?.ringtoneType,
      },
    },
  });

  const callGroupId = values.callGroupId ?? '';

  const { data: selectedCallGroup, isLoading: callGroupIsLoading } = useScopedQuery({
    queryKey: queryKeys.callGroup(callGroupId),
    queryFn: () => CallGroupApi.readCallGroup({ callGroupId: callGroupId }),
    enabled: !!callGroupId,
  });

  const processedDevices: PreviewDeviceObject[] = useMemo(() => {
    const devices = selectedCallGroup?.devices?.reduce<PreviewDeviceObject[]>((acc, device) => {
      if ('deskPhone' in device) {
        return [
          ...acc,
          {
            name: device.deskPhone.name,
            type: DeviceType.Desk,
            deviceId: device.deskPhone.deviceId,
          },
        ];
      } else if ('softPhone' in device) {
        return [
          ...acc,
          {
            name: device.softPhone.name,
            type: DeviceType.Softphone,
            deviceId: device.softPhone.deviceId,
          },
        ];
      } else if ('mobilePhone' in device) {
        return [
          ...acc,
          {
            name: device.mobilePhone.name,
            type: DeviceType.Mobile,
            deviceId: device.mobilePhone.deviceId,
          },
        ];
      } else if ('forwardingNumber' in device) {
        return [
          ...acc,
          {
            name: device.forwardingNumber.name,
            type: DeviceType.ForwardingNumber,
            forwardingNumberId: device.forwardingNumber.forwardingNumberId,
          },
        ];
      } else {
        return acc;
      }
    }, []);

    return (devices ?? []).sort((a, b) => a.name.localeCompare(b.name));
  }, [selectedCallGroup]);

  const handleProceedClick = useCallback(() => {
    const callGroup = callGroups.find((group) => group.id === values.callGroupId);

    if (!callGroup) {
      alerts.error('Unknown call group data');
      sentry.error({
        error: 'Call group not found when proceeding with call group step',
        topic: 'phone-config',
        team: 'fe-phone-config',
        severityLevel: 'error',
        addContext: {
          name: 'Step data',
          context: {
            callGroupId: values.callGroupId,
          },
        },
      });
      return;
    }

    const deviceNames = processedDevices.map((device) => device.name);

    const selectedRingtone = ringtones.find((r) => r.ringtoneType === values.ringtone);
    const isDefaultSelected = values.ringtone === DEFAULT_RINGTONE_VALUE;

    onProceedClick({
      type: CallRouteV1.Types.NodeType.NODE_TYPE_CALL_GROUP,
      callObject: {
        primitiveId: callGroup.id,
        primitiveName: callGroup.name,
        instructionId: initialState?.callObject?.instructionId ?? '',
        instructionSetId: initialState?.callObject?.instructionSetId ?? '',
        callGroupExpansion: {
          callerLabel: values.callerLabel,
          deviceCount: deviceNames.length,
          deviceNames,
          ringtone: isDefaultSelected
            ? {
                ringtoneType: Ringtone.RINGTONE_UNSPECIFIED,
                displayName: 'Default Ringtone',
              }
            : selectedRingtone
            ? {
                ringtoneType: selectedRingtone.ringtoneType,
                displayName: getConsolidatedDisplayName({
                  filteredRingtones,
                  selectedRingtone,
                  selectedRingtoneType: values.ringtone,
                }),
              }
            : undefined,
        },
      },
    });
  }, [
    callGroups,
    values.callGroupId,
    alerts,
    processedDevices,
    onProceedClick,
    initialState?.callObject?.instructionId,
    initialState?.callObject?.instructionSetId,
    values.callerLabel,
    values.ringtone,
    ringtones,
    filteredRingtones,
  ]);

  const handleOnCreateSuccess = (data: CreateResponse) => {
    const newCallGroupId = data.callGroupId;

    getFieldProps('callGroupId').onChange({
      // @ts-expect-error This is complaining thinking that name and value do not exist on the event target,
      // but they do.
      target: { name: 'callGroupId', value: newCallGroupId },
    });
  };

  return (
    <>
      <Step title={t('Call Group')} primaryButtonDisabled={!isComplete} onPrimaryButtonClick={handleProceedClick}>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: var(--fabric-space-gap-lg);
            max-width: 318px;
          `}
        >
          <SkeletonLoaders.Loader isLoading={callGroupsIsLoading} shape='rectangle' height={50}>
            <DropdownField
              label={t('Select Call Group')}
              {...getFieldProps('callGroupId')}
              placeholder={t('Select Call Group')}
              css={css`
                max-width: 318px;
                margin-top: var(--fabric-space-gap-sm);
              `}
            >
              <DropdownField.Option
                css={css`
                  padding: 0;
                `}
                key={'create-call-group'}
                value={''}
                isSelectable={false}
              >
                <Button
                  iconName='plus'
                  variant='tertiary'
                  onClick={handleOpenCreateCallGroup}
                  css={css`
                    width: 100%;
                    justify-content: start;
                  `}
                >
                  {t('Create Call Group')}
                </Button>
              </DropdownField.Option>
              {callGroups.map((group) => (
                <DropdownField.Option key={group.id} value={group.id}>
                  {group.name}
                </DropdownField.Option>
              ))}
            </DropdownField>
          </SkeletonLoaders.Loader>

          <SkeletonLoaders.Loader isLoading={callGroupsIsLoading} shape='rectangle' height={50}>
            <TextField
              label={t('Caller Label (Optional)')}
              {...getFieldProps('callerLabel')}
              helperText={getRemainingCharacters(values.callerLabel)}
              css={css`
                max-width: 318px;
                margin-top: var(--fabric-space-gap-sm);
              `}
            />
            <CallerLabelBaseText value={values.callerLabel} error={getFieldProps('callerLabel').error} />
          </SkeletonLoaders.Loader>

          <SkeletonLoaders.Loader isLoading={callGroupsIsLoading} shape='rectangle' height={50}>
            <div
              css={css`
                position: relative;
                display: flex;
                flex-direction: column;
                gap: var(--fabric-space-gap-sm);
              `}
            >
              <RingtoneDropdown
                {...getFieldProps('ringtone')}
                label={t('Select Desk Phone Ringtone (Optional)')}
                helperText={t('Choose a distinct ringtone so your staff can identify the purpose of the call')}
                ringtones={filteredRingtones}
                consolidateRingtoneType
              />

              <span
                css={css`
                  display: inline-flex;
                  position: absolute;
                  top: 8px;
                  right: -52px;
                `}
              ></span>
              <InlineNotification
                status='info'
                size='small'
                message={t(
                  'The ringtone options listed are for desk phones only. Wireless handsets will ring differently. Softphones will not be affected.'
                )}
                customStyles={css`
                  p {
                    white-space: normal;
                    overflow: visible;
                    text-overflow: unset;
                  }
                `}
              />
            </div>
          </SkeletonLoaders.Loader>
        </div>

        <DevicesPreview
          callObjectName={t('Call Group')}
          isLoading={callGroupIsLoading}
          showInitialState={!callGroupId}
          devices={processedDevices}
          locationResolver={locationResolver}
          editButton={
            selectedCallGroup ? (
              <Button
                variant='secondary'
                onClick={() => callGroupTrayModalControl.openModal()}
                css={css`
                  align-self: flex-start;
                `}
                trackingId='phone::setting::call-route:edit-callgroup-btn'
              >
                <Icon name='edit-small' />
                {t('Edit {{callObjectName}}', {
                  callObjectName: t('Call Group'),
                })}
              </Button>
            ) : null
          }
        />
      </Step>

      {settingsTenantLocation && (
        <CreateCallGroupModal
          tenantLocation={settingsTenantLocation}
          devices={sipProfiles}
          callForwardingNumbers={callForwardingNumbers}
          onSuccess={handleOnCreateSuccess}
          modalProps={createCallGroupModalProps}
        />
      )}

      {/* Call Group Settings Tray */}
      <CallGroupSettingTray
        modalControls={callGroupTrayModalControl}
        mountTarget='#call-route-page-body-container'
        selectedCallGroup={selectedCallGroup}
        stepContext={stepContext}
      />
    </>
  );
};
